import { useEffect, useMemo, useRef } from 'react'
import {
  Alert,
  Col,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Modal,
  Row,
  Select,
  Tooltip,
  Typography,
} from 'antd'
import type { Dayjs } from 'dayjs'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { usePermissions } from '@/shared/auth/usePermissions'
import { queryKeys } from '@/shared/api/queryKeys'
import { translateError } from '@/shared/api/errors'
import { useAuth } from '@/shared/auth/useAuth'
import { getCurrencyCode } from '@/shared/lib/format'
import { formatMoneyValue, type Money } from '@/shared/lib/money'
import { MoneyInput } from '@/shared/ui/MoneyInput'
import { withCurrentOption } from '@/shared/ui/currentOption'
import { submitOnValid } from '@/shared/lib/submitOnValid'
import {
  WRITE_OFF_KINDS,
  adjustStock,
  adjustmentDelta,
  balanceOf,
  canReceiveStock,
  fetchStockBalances,
  fetchStockPolicy,
  internalUseStock,
  isMainWarehouse,
  issueStock,
  newIdempotencyKey,
  receiveStock,
  dispatchTransfer,
  writeOffStock,
  type StockItem,
  type StockOperation,
  type StockTransfer,
  type Warehouse,
  type WriteOffKind,
} from './stockApi'

/**
 * Amal turi — panel ichidagi tushuncha.
 *
 * ⚠️ `MovementKind` BILAN CHALKASHTIRMANG: u JURNAL yozuvining turi
 * (ko'chirish IKKI qator yozadi — `TRANSFER_OUT` va `TRANSFER_IN`), bu
 * esa operator bajaradigan BITTA amal. Ularni birlashtirish
 * ko'chirishni ikki tugmaga bo'lib yuborardi.
 */
export const STOCK_ACTIONS = ['RECEIPT', 'ISSUE', 'TRANSFER', 'ADJUSTMENT', 'WRITE_OFF', 'INTERNAL_USE'] as const
export type StockAction = (typeof STOCK_ACTIONS)[number]

/**
 * Qaysi amal qaysi huquqni talab qiladi — `StockResource` bilan AYNAN
 * bir xil.
 *
 * Kirim, inventarizatsiya tuzatishi va hisobdan chiqarish `adjust` ostida: ular qoldiqni
 * kontragentsiz o'zgartiradi. Yetkazib beruvchi moli bosh ofis tasdiqlagan xarid orqali
 * kiradi. Ko'chirish jo'natiladi va oluvchi omborda alohida qabul qilinadi.
 */
export const ACTION_PERMISSION = {
  RECEIPT: 'inventory.stock.adjust',
  ISSUE: 'inventory.stock.write',
  TRANSFER: 'inventory.transfer.dispatch',
  ADJUSTMENT: 'inventory.stock.adjust',
  WRITE_OFF: 'inventory.stock.adjust',
  INTERNAL_USE: 'inventory.stock.internaluse',
} as const

export type StockActionResult =
  | { kind: 'operation'; operation: StockOperation }
  | { kind: 'transfer'; transfer: StockTransfer }

interface StockMovementModalProps {
  open: boolean
  action: StockAction
  warehouses: readonly Warehouse[]
  items: readonly StockItem[]
  /** Oldindan tanlangan ombor/mol (qoldiqlar jadvalidagi qatordan) */
  presetNodeId?: string | null
  presetItemId?: string | null
  onClose: () => void
  /** ⚠️ `replayed` va `lowStock` ni SAHIFA ko'rsatadi — ular modal yopilgandan keyin ham o'qilishi kerak */
  onDone: (result: StockActionResult, action: StockAction) => void
}

interface FormValues {
  nodeId: string
  toNodeId?: string
  itemId: string
  quantity?: number
  countedQuantity?: number
  unitCost?: Money | null
  unitPrice?: Money | null
  returnReason?: string
  returnCondition?: string
  serialNumbers?: string
  assetTag?: string
  writeOffKind?: WriteOffKind
  docNo?: string
  reason?: string
  occurredAt?: Dayjs | null
  /** Faqat `inventory.stock.adjust` amallarida — terib tasdiqlash */
  confirm?: string
}

/**
 * Ombor amali — kirim, chiqim, ko'chirish, inventarizatsiya, hisobdan
 * chiqarish.
 *
 * <h2>⚠️ BITTA OYNA, BESH AMAL — VA MAYDONLAR AMALGA QARAB</h2>
 * Backend beshta ALOHIDA buyruq shakli beradi (`StockCommand`) va
 * sababi u yerda yozilgan: "universal buyruq" har maydonni ixtiyoriy
 * qilib qo'yardi. Panel shu qarorni takrorlaydi — har amal uchun
 * o'zining maydonlari chiziladi, `null` bilan to'ldirilgan umumiy
 * forma emas.
 *
 * <h2>⚠️ MIQDOR HAR DOIM MUSBAT</h2>
 * Bazadagi ishora (`−` chiqim) — JURNALNING ichki shakli. Operatordan
 * ishorani so'rash bitta unutilgan minus bilan qoldiqni ikki barobar
 * oshirib yuborardi (`StockCommand` javadoc'i).
 *
 * <h2>⚠️ INVENTARIZATSIYA — SANALGAN MIQDOR, FARQ EMAS</h2>
 * Operator qo'lida sanoq varaqasi bo'ladi ("42 dona bor"), farq esa
 * hisoblanadigan narsa. Panel farqni KO'RSATADI, lekin serverga
 * SANALGAN son ketadi.
 *
 * <h2>⚠️ TAKRORIY SO'ROV KALITI BIR MARTA YASALADI</h2>
 * `QARORLAR.md` §20.9-D. Kalit oyna OCHILGANDA yasaladi va "Saqlash"
 * qayta bosilganda O'ZGARMAYDI — aks holda idempotentlikning ma'nosi
 * qolmaydi va tarmoq uzilib qayta urinilganda qoldiq ikki marta
 * o'zgarardi.
 *
 * <h2>⚠️ QOLDIQ YETMASLIGI — OLDINDAN KO'RSATILADI</h2>
 * Server 409 va aniq matn qaytaradi ("bor 5, so'raldi 8"), lekin
 * operator uni AMALDAN OLDIN ko'rishi kerak: chiqim oynasida joriy
 * qoldiq yozilib turadi va miqdor undan oshsa forma o'zi to'xtatadi.
 * Serverdagi tekshiruv baribir qoladi — u parallel ikki chiqimni
 * to'sadi, panel buni tuzilishi jihatidan qila olmaydi.
 *
 * <h2>⚠️ XATO OYNANING ICHIDA QOLADI</h2>
 * `VoucherVoidModal` naqshi. Ilgari xabar SAHIFA banneriga chiqarilardi,
 * oyna esa ochiq qolardi — matn modal niqobi ortida turardi va operator
 * faqat spinner to'xtaganini ko'rardi ("bosdim, hech narsa bo'lmadi").
 * Bu yerda u ayniqsa qimmat: 409 ning matni ("bor 5, so'raldi 8")
 * operator miqdorni TUZATISHI uchun kerak, ya'ni u aynan forma bilan
 * bir ekranda turishi shart.
 */
export function StockMovementModal({
  open,
  action,
  warehouses,
  items,
  presetNodeId,
  presetItemId,
  onClose,
  onDone,
}: StockMovementModalProps) {
  const { can } = usePermissions()
  const { t } = useTranslation()
  const { user } = useAuth()
  const [form] = Form.useForm<FormValues>()
  const queryClient = useQueryClient()
  const currency = user?.tenant.currency ?? getCurrencyCode()

  /** ⚠️ Oyna ochilganda BIR MARTA — qayta urinishda o'zgarmaydi */
  const idempotencyKey = useRef<string>(newIdempotencyKey())

  useEffect(() => {
    if (!open) return
    idempotencyKey.current = newIdempotencyKey()
    form.setFieldsValue({
      ...(presetNodeId ? { nodeId: presetNodeId } : { nodeId: undefined }),
      ...(presetItemId ? { itemId: presetItemId } : { itemId: undefined }),
      toNodeId: undefined,
      quantity: undefined,
      countedQuantity: undefined,
      unitCost: null,
      unitPrice: null,
      returnReason: '',
      returnCondition: '',
      serialNumbers: '',
      assetTag: '',
      writeOffKind: 'DAMAGED',
      docNo: '',
      reason: '',
      occurredAt: null,
      confirm: '',
    })
  }, [open, presetNodeId, presetItemId, form])

  // `Form.useWatch` — tanlangan ombor/mol bo'yicha joriy qoldiqni
  // so'rash uchun. `shouldUpdate` bilan qilish mumkin edi, lekin bu
  // yerda qiymat FORMA TASHQARISIDA (so'rovda) kerak.
  const nodeId = Form.useWatch('nodeId', form)
  const toNodeId = Form.useWatch('toNodeId', form)
  const itemId = Form.useWatch('itemId', form)
  const fromMain = isMainWarehouse(warehouses, nodeId)
  const priceDenied = action === 'TRANSFER' && fromMain && !can('inventory.transfer.price')
  const isReturn = action === 'TRANSFER' && Boolean(toNodeId) && isMainWarehouse(warehouses, toNodeId)

  const balances = useQuery({
    queryKey: queryKeys.stock.balances(nodeId ?? null, itemId ?? null, false),
    queryFn: ({ signal }) => fetchStockBalances(nodeId ?? null, itemId ?? null, false, signal),
    // ⚠️ `belowMinOnly` bu yerda HAR DOIM `false`: oynaga joriy qoldiq
    // kerak, "kam qolganlar ro'yxati" emas. `true` bo'lsa chegaradan
    // yuqori qoldiq umuman qaytmasdi va oyna uni "qator yo'q" deb
    // ko'rsatardi — ya'ni chiqimni yolg'on sabab bilan to'sardi.
    enabled: open && Boolean(nodeId) && Boolean(itemId),
  })

  const policy = useQuery({
    queryKey: queryKeys.stock.policy,
    queryFn: ({ signal }) => fetchStockPolicy(signal),
    enabled: open && action === 'INTERNAL_USE',
  })
  const internalUseThreshold = action === 'INTERNAL_USE' && policy.isSuccess ? policy.data.internalUseThreshold : null

  const current = useMemo(
    () => balanceOf(balances.data ?? [], nodeId ?? null, itemId ?? null),
    [balances.data, nodeId, itemId],
  )

  const selectedItem = items.find((item) => item.id === itemId) ?? null
  const unit = selectedItem?.unit ?? ''

  /**
   * Chiqim tomonidagi mavjud qoldiq.
   *
   * ⚠️ `null` — "hali noma'lum" (so'rov ketmagan yoki yuklanmoqda),
   * `0` — "bor edi, tugadi". Ikkalasini bir xil ko'rsatish
   * "yetarli emas" degan YOLG'ON xabar berardi — hali hech narsa
   * so'ralmagan bo'lsa ham.
   */
  const available = current === null ? null : current.quantity

  const operationFor = (
    kind: Exclude<StockAction, 'TRANSFER'>,
    values: FormValues,
    common: { docNo: string | null; reason: string | null; occurredAt: string | null; idempotencyKey: string },
  ): Promise<StockOperation> => {
    switch (kind) {
      case 'RECEIPT':
        return receiveStock({
          ...common,
          nodeId: values.nodeId,
          itemId: values.itemId,
          quantity: values.quantity ?? 0,
          // ⚠️ MINOR birlik. Bepul kirim bo'lsa 0 — `null` EMAS:
          // server `@NotNull` talab qiladi va "tannarx noma'lum"
          // degan holat bu modelda yo'q.
          unitCostMinor: values.unitCost?.amount ?? 0,
        })
      case 'ISSUE':
        return issueStock({
          ...common,
          nodeId: values.nodeId,
          itemId: values.itemId,
          quantity: values.quantity ?? 0,
        })
      case 'ADJUSTMENT':
        return adjustStock({
          ...common,
          nodeId: values.nodeId,
          itemId: values.itemId,
          countedQuantity: values.countedQuantity ?? 0,
          reason: values.reason?.trim() ?? '',
        })
      case 'WRITE_OFF':
        return writeOffStock({
          ...common,
          nodeId: values.nodeId,
          itemId: values.itemId,
          quantity: values.quantity ?? 0,
          kind: values.writeOffKind ?? 'DAMAGED',
          reason: values.reason?.trim() ?? '',
        })
      case 'INTERNAL_USE':
        return internalUseStock({
          nodeId: values.nodeId,
          itemId: values.itemId,
          quantity: values.quantity ?? 0,
          reason: values.reason?.trim() ?? '',
          assetTag: values.assetTag?.trim() ?? '',
          idempotencyKey: common.idempotencyKey,
        })
    }
  }

  const submit = useMutation({
    mutationFn: async (values: FormValues): Promise<StockActionResult> => {
      const common = {
        docNo: values.docNo?.trim() ? values.docNo.trim() : null,
        reason: values.reason?.trim() ? values.reason.trim() : null,
        occurredAt: values.occurredAt ? values.occurredAt.toISOString() : null,
        idempotencyKey: idempotencyKey.current,
      }

      if (action === 'TRANSFER') {
        const transfer = await dispatchTransfer({
          fromNodeId: values.nodeId,
          toNodeId: values.toNodeId ?? '',
          itemId: values.itemId,
          quantity: values.quantity ?? 0,
          unitPriceMinor: fromMain ? (values.unitPrice?.amount ?? 0) : null,
          returnReason: isReturn ? (values.returnReason?.trim() ?? '') : null,
          returnCondition: isReturn ? (values.returnCondition?.trim() ?? '') : null,
          serialNumbers: isReturn && values.serialNumbers?.trim() ? values.serialNumbers.trim() : null,
          docNo: common.docNo,
          note: common.reason,
          idempotencyKey: idempotencyKey.current,
        })
        return { kind: 'transfer', transfer }
      }
      return { kind: 'operation', operation: await operationFor(action, values, common) }
    },
    onSuccess: (result) => {
      // ⚠️ `stock.all` PREFIKS: bitta harakat qoldiqni ham, jurnalni
      // ham, zaxira qiymatini ham eskirtiradi.
      void queryClient.invalidateQueries({ queryKey: queryKeys.stock.all })
      form.resetFields()
      onDone(result, action)
      onClose()
    },
  })

  /** Bekor qilib chiqish — xato xabari ham qolmasin */
  const close = () => {
    submit.reset()
    onClose()
  }

  const warehouseOption = (warehouse: Warehouse) => ({
    value: warehouse.id,
    label: `${warehouse.displayName} (${warehouse.code})`,
  })

  /**
   * Ombor tanlovi IKKI XIL, va farq YO'NALISHDA — sabab
   * `canReceiveStock` javadoc'ida. Ilgari ikkala maydon ham bitta,
   * FILTRSIZ ro'yxatdan to'ldirilardi va yopilgan ombor kirim manzili
   * bo'lib tanlanishi mumkin edi.
   */
  const sourceOptions = warehouses.map(warehouseOption)
  const inboundOptions = warehouses.filter(canReceiveStock)
    .filter((warehouse) => !nodeId || isMainWarehouse(warehouses, warehouse.id) !== fromMain)
    .map(warehouseOption)

  /**
   * ⚠️ Nofaol mol yangi harakatda TANLANMAYDI — server ham rad etadi
   * ({@code StockException.ItemInactive}, `StockFlowTest` da mixlangan).
   *
   * ⚠️ LEKIN FILTR YOLG'IZ YETMAYDI (N8, 2026-08-15). Qiymat bu yerga
   * TASHQARIDAN keladi (`presetItemId` — qoldiqlar jadvalidagi
   * qatordan), va nofaol molning qoldig'i JOYIDA qoladi, ya'ni qator
   * ham, undagi «Count» tugmasi ham bor. Mos `option` topilmaganda
   * antd `Select` XOM `value` ni chizadi va ekranga UUID chiqadi —
   * Staging'da aynan shunday o'lchandi
   * (`cf59ed62-69d5-48cd-bf33-ec269afd48bd`).
   *
   * Shuning uchun joriy qiymat ro'yxatga QAYTARILADI, lekin
   * `disabled` bilan: yorliq ko'rinadi, tanlash mumkin emas.
   * Tafsilot — `shared/ui/currentOption.ts`.
   */
  const itemOptions = withCurrentOption(
    items
      .filter((item) => item.active)
      .map((item) => ({ value: item.id, label: `${item.name} · ${item.sku}` })),
    itemId,
    (id) => {
      const missing = items.find((item) => item.id === id)
      return missing === undefined
        ? null
        : {
            value: missing.id,
            label: `${missing.name} · ${missing.sku} · ${t('stock.item.stateInactive')}`,
            disabled: true,
          }
    },
  )

  /**
   * Tanlangan mol nofaol — amal MUMKIN EMAS.
   *
   * ⚠️ Server baribir rad etadi, lekin operator buni AMALDAN OLDIN
   * ko'rishi kerak — shu fayldagi "qoldiq yetmasligi" qarori bilan
   * BIR XIL sabab: to'ldirilgan formani yuborib, sababi tushunarsiz
   * 4xx olish eng yomon yo'l.
   *
   * ⚠️ Tekshiruv OYNADA, sahifadagi tugmalarda EMAS. Beshala amal shu
   * oynaga kelib qo'shiladi (jadval qatoridan uchtasi, sarlavhadan
   * ikkitasi), ya'ni bitta joyda yozilgan qoida hammasini yopadi.
   * Tugmalarni sahifada o'chirish qoidani IKKINCHI marta yozardi va
   * beshtadan uchtasini qamrardi.
   */
  const itemInactive = selectedItem !== null && !selectedItem.active

  const outbound = action === 'ISSUE' || action === 'TRANSFER' || action === 'WRITE_OFF' || action === 'INTERNAL_USE'
  const reasonRequired = action === 'ADJUSTMENT' || action === 'WRITE_OFF' || action === 'INTERNAL_USE'

  /**
   * XAVFLI AMAL — qoldiqni KONTRAGENTSIZ o'zgartiradi.
   *
   * ⚠️ Shart huquq bo'yicha yoziladi, amal nomlari ro'yxati bilan emas:
   * `ACTION_PERMISSION` allaqachon "kontragentsiz" degan chegarani
   * `inventory.stock.adjust` da chizib qo'ygan (o'sha izohga qarang).
   * Ro'yxatni ikkinchi marta yozish yangi amal qo'shilganda ikkalasini
   * birga yangilashni talab qilardi va bittasi unutilardi.
   */
  const dangerous = ACTION_PERMISSION[action] === 'inventory.stock.adjust'

  const selectedWarehouse = warehouses.find((warehouse) => warehouse.id === nodeId) ?? null
  const typed = Form.useWatch('confirm', form)

  /**
   * ⚠️ UCHINCHI JOY, VA BU YERDA NUQSON KO'RINISHDAN CHUQURROQ
   * (N8 qamrovi, 2026-08-15).
   *
   * Kirim manzili `inboundOptions` dan to'ldiriladi, ya'ni YOPILGAN
   * ombor ro'yxatda yo'q. Lekin `presetNodeId` sahifadagi ombor
   * filtridan keladi va u filtr YOPILGANLARNI ham ko'rsatadi
   * (`sourceOptions` bilan bir xil ro'yxat — chiqim uchun ular
   * KERAK). Natijada "kirim" oynasi yopilgan ombor bilan ochilardi
   * va IKKI narsa yuz berardi:
   *
   *   1. yorliq topilmay ekranga XOM UUID chiqardi;
   *   2. «Saqlash» ISHLARDI — server bu shartni tekshirmaydi
   *      (`canReceiveStock` javadoc'i: "bu — panelning o'z qorovuli
   *      va u yolg'iz turadi"). Ya'ni oldindan qo'yilgan qiymat
   *      panelning YAGONA qorovulini jimgina aylanib o'tardi.
   *
   * Shuning uchun yorliq qaytariladi (`disabled`), va bu holatda
   * «Saqlash» to'siladi. Bu YANGI qoida emas — u allaqachon
   * `inboundOptions` da yozilgan, faqat teshigi yopildi.
   */
  const warehouseOptions =
    action === 'RECEIPT'
      ? withCurrentOption(inboundOptions, nodeId, (id) => {
          const missing = warehouses.find((warehouse) => warehouse.id === id)
          return missing === undefined
            ? null
            : { ...warehouseOption(missing), label: `${warehouseOption(missing).label} · ${t('stock.field.warehouseClosed')}`, disabled: true }
        })
      : sourceOptions

  const warehouseClosed =
    action === 'RECEIPT' && selectedWarehouse !== null && !canReceiveStock(selectedWarehouse)

  /**
   * Terib tasdiqlash — `NasDeleteModal` / `CancelPaymentModal` naqshi.
   *
   * ⚠️ NEGA AYNAN OMBOR KODI. `VoucherVoidModal` bu naqsh qachon
   * QOROVUL, qachon MAROSIM ekanini ajratib qo'ygan: terish kerak
   * bo'lgan qiymat operator YODIDA turadigan nom bo'lsa — qorovul;
   * tasodifiy kod bo'lsa — uni ko'chirib qo'yish bir bosishlik ish.
   * Ombor kodi (`MAIN-WH`) birinchi turdan: u qisqa, operator uni
   * biladi va aynan shu tanlov — "qaysi omborning qoldig'ini
   * o'zgartiryapman" — bu yerdagi eng qimmat xato.
   *
   * ⚠️ Faqat `adjust` amallarida. Kirim va chiqimda kontragent bor
   * (yetkazib beruvchi hujjati, chiqim qog'ozi), ya'ni yozuvni
   * tashqaridan tekshirish mumkin — u yerda tasdiq marosim bo'lardi.
   */
  const confirmed =
    !dangerous || (selectedWarehouse !== null && typed?.trim() === selectedWarehouse.code)

  /**
   * ⚠️ QOLDIQDAN OSHIQ MIQDOR — MAYDON VALIDATORIDAN MUSTAQIL
   * (BUG-027, tester hisoboti 2).
   *
   * Maydonda validator allaqachon bor va u miqdor TERILGANDA ishlaydi.
   * Lekin qoldiq forma qiymati EMAS — u alohida so'rovdan keladi
   * (`balances`), ya'ni u miqdordan KEYIN o'zgarishi mumkin: operator
   * "5" yozib qo'yib, keyin omborni yoki molni almashtiradi.
   *
   * ⚠️ 2026-08-16 da Staging'da AYNAN shu holat o'lchandi: 20 donali
   * ombordan 5 dona ko'chirish yozilgan, keyin manba ombori 0 donaliga
   * almashtirilgan — maydon xatosi CHIQMADI, «Saqlash» OCHIQ qoldi va
   * xabar faqat tugma bosilgandan keyin ko'rindi. Tekshiruvchi buni
   * "inconsistent validation" deb yozgan va u AYNAN shu.
   *
   * ⚠️ antd `dependencies` bu yerda YETMAYDI: u maydon o'zgarganda
   * qayta tekshiradi, qoldiq esa o'shanda hali KELMAGAN bo'ladi
   * (so'rov ketyapti). Shuning uchun shart TO'G'RIDAN-TO'G'RI kuzatilgan
   * qiymatlardan hisoblanadi — u har renderda yangi va hech qachon
   * eskirmaydi.
   */
  const quantityTyped = Form.useWatch('quantity', form)
  const exceedsStock =
    outbound &&
    available !== null &&
    typeof quantityTyped === 'number' &&
    quantityTyped > available

  /**
   * ⚠️ «SAQLASH» NEGA O'CHIRILGAN — TUGMANING O'ZIDA (BUG-028, BUG-029).
   *
   * Tekshiruvchining ikki bandi bir xil: «WAREHOUSE BUTTON IS DISABLED
   * WITHOUT FEEDBACK WHEN HOVERED OVER THE BUTTON». 2026-08-16 da
   * o'lchandi: tugma rostdan `disabled`, `title` atributi YO'Q, ustiga
   * olib borilganda hech qanday tooltip chiqmaydi. Sabab ekranda BOR
   * edi, lekin formaning eng pastida — tugmadan uzoqda va ko'z bilan
   * bog'lanmagan holda.
   *
   * ⚠️ Sabab BITTA qaytariladi, ro'yxat emas: operatorga «keyingi
   * qadam» kerak, inventarizatsiya emas. Tartib to'siqning OG'IRLIGI
   * bo'yicha — nofaol mol va yopilgan ombor umuman boshqa tanlov
   * talab qiladi, qolgan ikkitasi esa shu formada tuzatiladi.
   *
   * ⚠️ Matnlar YANGI EMAS — mavjud kalitlar qayta ishlatiladi. Ikkinchi
   * nusxa yozilsa, bir kun ekrandagi va tugmadagi sabab boshqa-boshqa
   * gapirardi.
   */
  const blockedReason: string | null = priceDenied
    ? t('stock.validation.transferPriceDenied')
    : itemInactive
    ? t('stock.item.inactiveSelected')
    : warehouseClosed
      ? t('stock.field.warehouseClosedSelected')
      : exceedsStock
        ? t('stock.validation.insufficient', { available, unit })
        : !confirmed
          ? selectedWarehouse === null
            ? t('stock.field.confirmPickWarehouse')
            : t('actions.typeToConfirm', { value: selectedWarehouse.code })
          : null

  return (
    <Modal
      open={open}
      title={t(`stock.action.${action}.title`)}
      onCancel={close}
      onOk={() => submitOnValid(form, (values) => submit.mutate(values))}
      confirmLoading={submit.isPending}
      okText={t(`stock.action.${action}.submit`)}
      okButtonProps={{
        danger: action === 'WRITE_OFF',
        disabled: blockedReason !== null,
      }}
      cancelText={t('common.cancel')}
      /*
        ⚠️ FUTER QAYTA CHIZILADI, LEKIN TUGMALAR ANTD NIKI (`OkBtn` /
        `CancelBtn`): shu tufayli `okText`, `onOk`, `confirmLoading` va
        `okButtonProps` yuqoridagi joyida QOLADI. Qo'lda `<Button>`
        yozilganda ular ikkinchi marta ta'riflanardi va bittasi bir kun
        ajralib ketardi.

        ⚠️ `<span>` MAJBURIY: `disabled` tugmada `pointer-events: none`
        turadi, ya'ni sichqoncha hodisasi tugmaga UMUMAN yetib bormaydi
        va tooltip hech qachon chiqmasdi — nuqson tuzatilgandek
        ko'rinib, aslida joyida qolardi.
      */
      footer={(_, { OkBtn, CancelBtn }) => (
        <>
          <CancelBtn />
          <Tooltip title={blockedReason}>
            <span style={{ display: 'inline-block' }}>
              <OkBtn />
            </span>
          </Tooltip>
        </>
      )}
      destroyOnHidden
      width={680}
    >
      <Form<FormValues> form={form} layout="vertical" preserve={false} disabled={submit.isPending}>
        <Alert
          type={action === 'WRITE_OFF' || action === 'ADJUSTMENT' ? 'warning' : 'info'}
          showIcon
          style={{ marginBottom: 16 }}
          message={t(`stock.action.${action}.note`)}
        />

        {internalUseThreshold === null ? null : (
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
            message={
              internalUseThreshold.amount > 0
                ? t('stock.action.INTERNAL_USE.approvalNote', {
                    threshold: formatMoneyValue(internalUseThreshold),
                  })
                : t('stock.action.INTERNAL_USE.approvalOff')
            }
          />
        )}

        {/*
          Nofaol mol — amal MUMKIN EMAS va sababi DARHOL aytiladi.
          Qoldiq qatori joyida qolgani uchun operator bu oynaga
          «Count»/«Issue» tugmasi orqali baribir kirib kela oladi.
        */}
        {itemInactive ? (
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 16 }}
            message={t('stock.item.inactiveSelected')}
          />
        ) : null}

        {/* Yopilgan ombor kirim MANZILI bo'la olmaydi — `canReceiveStock` */}
        {warehouseClosed ? (
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 16 }}
            message={t('stock.field.warehouseClosedSelected')}
          />
        ) : null}

        {/*
          ⚠️ QOLDIQ YETMASLIGI — TEPADA HAM, DARHOL (BUG-027).

          Maydon ostidagi xato faqat miqdor TERILGANDA chiqadi. Ombor
          yoki mol almashganda esa o'zgargan narsa QOLDIQ — forma
          maydoni emas — va antd u haqda hech narsa bilmaydi. Bu banner
          `exceedsStock` dan chiqadi, ya'ni u qoldiq KELGAN zahoti
          ko'rinadi va tugmaning o'chishi bilan bir vaqtda paydo bo'ladi.
        */}
        {exceedsStock ? (
          <Alert
            type="error"
            showIcon
            style={{ marginBottom: 16 }}
            message={t('stock.validation.insufficient', { available, unit })}
          />
        ) : null}

        {/* 409 ("bor 5, so'raldi 8") va boshqalar SHU YERDA qoladi */}
        {submit.isError ? (
          <Alert
            type="error"
            showIcon
            style={{ marginBottom: 16 }}
            message={translateError(submit.error, t)}
          />
        ) : null}

        <Row gutter={16}>
          <Col xs={24} md={12}>
            <Form.Item
              name="nodeId"
              label={t(action === 'TRANSFER' ? 'stock.field.fromWarehouse' : 'stock.field.warehouse')}
              extra={t('stock.field.warehouseHint')}
              rules={[{ required: true, message: t('stock.validation.warehouseRequired') }]}
            >
              <Select
                showSearch
                optionFilterProp="label"
                options={warehouseOptions}
                notFoundContent={t('stock.field.noWarehouses')}
              />
            </Form.Item>
          </Col>

          {action === 'TRANSFER' ? (
            <Col xs={24} md={12}>
              <Form.Item
                name="toNodeId"
                label={t('stock.field.toWarehouse')}
                extra={t('stock.field.toWarehouseHint')}
                dependencies={['nodeId']}
                rules={[
                  { required: true, message: t('stock.validation.toWarehouseRequired') },
                  ({ getFieldValue }) => ({
                    validator: (_rule, value: string | undefined) =>
                      // ⚠️ Server ham 400 beradi (`SameNodeTransfer`),
                      // lekin oldindan aytish operatorni bekor
                      // urinishdan saqlaydi.
                      value && value === getFieldValue('nodeId')
                        ? Promise.reject(new Error(t('stock.validation.sameWarehouse')))
                        : Promise.resolve(),
                  }),
                ]}
              >
                <Select showSearch optionFilterProp="label" options={inboundOptions} />
              </Form.Item>
            </Col>
          ) : null}

          <Col xs={24} md={12}>
            <Form.Item
              name="itemId"
              label={t('stock.field.item')}
              rules={[{ required: true, message: t('stock.validation.itemRequired') }]}
            >
              <Select
                showSearch
                optionFilterProp="label"
                options={itemOptions}
                notFoundContent={t('stock.item.empty')}
              />
            </Form.Item>
          </Col>
        </Row>

        {/*
          Joriy qoldiq — amaldan OLDIN. Ikki holat ATAYLAB farqlanadi:
          qator umuman yo'q (mol bu omborda hech qachon bo'lmagan) va
          nol (bor edi, tugadi).
        */}
        {nodeId && itemId ? (
          <Typography.Paragraph type="secondary" style={{ marginBottom: 16 }}>
            {balances.isPending
              ? t('common.loading')
              : available === null
                ? t('stock.field.noBalanceRow')
                : t('stock.field.currentBalance', { quantity: available, unit })}
          </Typography.Paragraph>
        ) : null}

        <Row gutter={16}>
          {action === 'ADJUSTMENT' ? (
            <Col xs={24} md={12}>
              <Form.Item
                name="countedQuantity"
                label={t('stock.field.countedQuantity')}
                extra={t('stock.field.countedQuantityHint')}
                rules={[{ required: true, message: t('stock.validation.countedRequired') }]}
              >
                <InputNumber min={0} precision={0} style={{ width: '100%' }} addonAfter={unit} />
              </Form.Item>
            </Col>
          ) : (
            <Col xs={24} md={12}>
              <Form.Item
                name="quantity"
                label={t('stock.field.quantity')}
                extra={t('stock.field.quantityHint')}
                rules={[
                  { required: true, message: t('stock.validation.quantityRequired') },
                  {
                    validator: (_rule, value: number | null) => {
                      if (value === null || value === undefined) return Promise.resolve()
                      if (value <= 0) {
                        return Promise.reject(new Error(t('stock.validation.quantityPositive')))
                      }
                      // ⚠️ Faqat chiqim tomonida va faqat qoldiq
                      // MA'LUM bo'lganda. Noma'lum qoldiqda taqiqlash
                      // "yetarli emas" degan yolg'on xabar bo'lardi.
                      if (outbound && available !== null && value > available) {
                        return Promise.reject(
                          new Error(t('stock.validation.insufficient', { available, unit })),
                        )
                      }
                      return Promise.resolve()
                    },
                  },
                ]}
              >
                <InputNumber min={1} precision={0} style={{ width: '100%' }} addonAfter={unit} />
              </Form.Item>
            </Col>
          )}

          {priceDenied ? (
            <Col xs={24}>
              <Alert type="warning" showIcon message={t('stock.validation.transferPriceDenied')} />
            </Col>
          ) : null}

          {action === 'TRANSFER' && fromMain && !priceDenied ? (
            <Col xs={24} md={12}>
              <Form.Item
                name="unitPrice"
                label={t('stock.field.transferPrice')}
                extra={t('stock.field.transferPriceHint')}
                rules={[{ required: true, message: t('stock.validation.transferPriceRequired') }]}
              >
                <MoneyInput currency={currency} />
              </Form.Item>
            </Col>
          ) : null}

          {action === 'RECEIPT' ? (
            <Col xs={24} md={12}>
              <Form.Item
                name="unitCost"
                label={t('stock.field.unitCost')}
                extra={t('stock.field.unitCostHint')}
                rules={[{ required: true, message: t('stock.validation.unitCostRequired') }]}
              >
                {/* ⚠️ Major↔minor aylantirish FAQAT shu komponent ichida */}
                <MoneyInput currency={currency} />
              </Form.Item>
            </Col>
          ) : null}

          {action === 'WRITE_OFF' ? (
            <Col xs={24} md={12}>
              <Form.Item
                name="writeOffKind"
                label={t('stock.field.writeOffKind')}
                extra={t('stock.field.writeOffKindHint')}
                rules={[{ required: true, message: t('stock.validation.writeOffKindRequired') }]}
              >
                <Select
                  options={WRITE_OFF_KINDS.map((kind) => ({
                    value: kind,
                    label: t(`movementKind.${kind}`),
                  }))}
                />
              </Form.Item>
            </Col>
          ) : null}
        </Row>

        {/*
          Inventarizatsiya farqi — KO'RSATILADI, yuborilmaydi.
          ⚠️ Nol farqda server HECH QANDAY harakat yozmaydi (nol yozuv —
          shovqin). Buni oldindan aytamiz, aks holda operator "bosdim,
          jurnalda yo'q" holatiga tushardi.
        */}
        {action === 'ADJUSTMENT' ? (
          <Form.Item
            noStyle
            shouldUpdate={(prev: { countedQuantity?: number }, next: { countedQuantity?: number }) =>
              prev.countedQuantity !== next.countedQuantity
            }
          >
            {({ getFieldValue }) => {
              const counted = getFieldValue('countedQuantity') as number | undefined
              if (available === null || counted === undefined || counted === null) return null
              const delta = adjustmentDelta(available, counted)
              return (
                <Alert
                  type={delta === 0 ? 'warning' : 'info'}
                  showIcon
                  style={{ marginBottom: 16 }}
                  message={
                    delta === 0
                      ? t('stock.field.deltaZero')
                      : t('stock.field.delta', {
                          current: available,
                          counted,
                          delta: delta > 0 ? `+${String(delta)}` : String(delta),
                          unit,
                        })
                  }
                />
              )
            }}
          </Form.Item>
        ) : null}

        {isReturn ? (
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item
                name="returnReason"
                label={t('stock.field.returnReason')}
                rules={[
                  { required: true, whitespace: true, message: t('stock.validation.returnReasonRequired') },
                  { max: 500, message: t('stock.validation.reasonLength') },
                ]}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name="returnCondition"
                label={t('stock.field.returnCondition')}
                extra={t('stock.field.returnConditionHint')}
                rules={[
                  { required: true, whitespace: true, message: t('stock.validation.returnConditionRequired') },
                  { max: 200, message: t('stock.validation.returnConditionLength') },
                ]}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col xs={24}>
              <Form.Item
                name="serialNumbers"
                label={t('stock.field.serialNumbers')}
                extra={t('stock.field.serialNumbersHint')}
                rules={[{ max: 2000, message: t('stock.validation.serialNumbersLength') }]}
              >
                <Input.TextArea autoSize={{ minRows: 1, maxRows: 4 }} />
              </Form.Item>
            </Col>
          </Row>
        ) : null}

        {action === 'INTERNAL_USE' ? (
          <Form.Item
            name="assetTag"
            label={t('stock.field.assetTag')}
            extra={t('stock.field.assetTagHint')}
            rules={[
              { required: true, whitespace: true, message: t('stock.validation.assetTagRequired') },
              { max: 128, message: t('stock.validation.assetTagLength') },
            ]}
          >
            <Input maxLength={128} />
          </Form.Item>
        ) : (
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item name="docNo" label={t('stock.field.docNo')} extra={t('stock.field.docNoHint')}>
                <Input maxLength={64} />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name="occurredAt"
                label={t('stock.field.occurredAt')}
                extra={t('stock.field.occurredAtHint')}
              >
                <DatePicker showTime style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>
        )}

        <Form.Item
          name="reason"
          label={t('stock.field.reason')}
          extra={reasonRequired ? t('stock.field.reasonRequiredHint') : t('stock.field.reasonHint')}
          rules={
            reasonRequired
              ? [
                  { required: true, whitespace: true, message: t('stock.validation.reasonRequired') },
                  { max: 500, message: t('stock.validation.reasonLength') },
                ]
              : [{ max: 500, message: t('stock.validation.reasonLength') }]
          }
        >
          <Input.TextArea rows={2} />
        </Form.Item>

        {/*
          ⚠️ XAVFLI AMAL — TERIB TASDIQLASH. Sabab `confirmed` izohida.
          Maydon eng OXIRIDA: operator avval nima yozayotganini
          to'ldiradi, tasdiq esa oxirgi qadam bo'ladi.
        */}
        {dangerous ? (
          <>
            <Form.Item
              name="confirm"
              label={t('stock.field.confirmLabel')}
              extra={t('stock.field.confirmHint')}
              rules={[{ required: true, message: t('stock.validation.confirmRequired') }]}
            >
              <Input autoComplete="off" placeholder={selectedWarehouse?.code ?? ''} />
            </Form.Item>
            <Typography.Text type="secondary">
              {selectedWarehouse === null
                ? t('stock.field.confirmPickWarehouse')
                : t('actions.typeToConfirm', { value: selectedWarehouse.code })}
            </Typography.Text>
          </>
        ) : null}
      </Form>
    </Modal>
  )
}
